/**
 * CharacterBatcher — every remote character through a handful of draw calls.
 *
 * The PropBatcher insight re-applied to bodies that MOVE: cost must scale with
 * KINDS of part, not with players × parts. A character assembled in proxy mode
 * (`assembleCharacter(bp, { proxy: true })`) is a pivot hierarchy of bare
 * Object3Ds, each carrying `{ prim, dims, colorHex, cls, outline, shadow }`.
 * This module keeps one `InstancedMesh` of a UNIT primitive per
 * `prim × class` pair, and every frame rewrites the per-instance matrices from
 * the posed hierarchy:
 *
 *     instanceMatrix[i] = part.matrixWorld × diag(part.dims)
 *
 * The pose driver only ever writes pivot transforms, so it cannot tell whether
 * a Mesh or a pool is drawing the result — the defeat scatter batches
 * identically because a scattered block is the same primitive with a different
 * matrix.
 *
 * Colours ride `instanceColor` (proven on WebGPU by the instanced platform
 * batches); the toon fill emissive rides the same varying via TSL. The hit
 * flash is per-instance by mixing the instance colour toward white — "plan B"
 * from CHARACTERS.md §5: less blowout than the local body's emissive push,
 * zero new shader surface.
 *
 * Outline hulls are pools too: the same unit geometries under the CheapOutline
 * fatten, with the per-axis unit-space thickness (0.045 world units ÷ the
 * part's dims) delivered per instance so a small fist and a big torso wear the
 * SAME world-thickness line the naive path draws.
 *
 * Every frame is rebuilt from scratch — counts reset, instances appended,
 * `count` set. At eight players × ~40 parts that is ~320 matrix multiplies,
 * unmeasurable next to the render, and it buys freedom from slot bookkeeping:
 * removing a character is deleting it from a Set.
 */

import * as THREE from 'three/webgpu';
import { HULL_THICKNESS } from '../../CheapOutline.js';
import {
  positionLocal,
  normalLocal,
  float,
  vec3,
  vec4,
  clamp,
  modelViewMatrix,
  varyingProperty,
  instancedDynamicBufferAttribute,
} from 'three/tsl';
import { GRADIENT_2 } from '../../ToonMaterials.js';

/** World-space hull thickness at the reference distance — CheapOutline's. */
const START_CAPACITY = 128;

/** Compromise toon fill for pooled bodies: the naive path varies it 0.14–0.26
 *  per family; one pool can only carry one intensity, so it takes the middle. */
const POOL_EMISSIVE = 0.2;

function unitGeometryFor(prim) {
  if (prim === 'box') return new THREE.BoxGeometry(1, 1, 1);
  if (prim === 'sphere') return new THREE.SphereGeometry(0.5, 12, 12);
  if (prim === 'cone') return new THREE.ConeGeometry(0.5, 1, 10);
  if (prim.startsWith('cyl:')) {
    const ratio = parseFloat(prim.slice(4)) || 1;
    return new THREE.CylinderGeometry(0.5 * ratio, 0.5, 1, 12);
  }
  return new THREE.BoxGeometry(1, 1, 1);
}

function makePoolMaterial(cls) {
  if (cls === 'unlit') {
    // The accent-material look (eyes, glyphs). Colour arrives per instance.
    return new THREE.MeshBasicNodeMaterial({ color: 0xffffff });
  }
  const mat = new THREE.MeshToonNodeMaterial({
    color: 0xffffff, // per-instance colour multiplies in; white keeps it true
    gradientMap: GRADIENT_2,
  });
  // The toon FILL from createPlayerToonMaterial, per instance: the renderer
  // assigns the instance colour to the `vInstanceColor` varying whenever
  // `instanceColor` is set, so the emissive can ride the same value the
  // diffuse gets multiplied by.
  mat.emissiveNode = vec3(varyingProperty('vec3', 'vInstanceColor')).mul(float(POOL_EMISSIVE));
  return mat;
}

/**
 * Hull pool material. Same fatten as CheapOutline/the naive assembler, except
 * the push happens in UNIT space, so the per-axis thickness (0.045 ÷ dims)
 * comes in through an instanced attribute rather than a constant.
 */
function makeHullMaterial(thickAttr) {
  const m = new THREE.MeshBasicNodeMaterial({ color: 0x05060d });
  const viewDepth = modelViewMatrix.mul(vec4(positionLocal, 1)).z.negate();
  const depthScale = clamp(viewDepth.div(10), float(1), float(8));
  const thickness = vec3(instancedDynamicBufferAttribute(thickAttr, 'vec3', 3, 0));
  m.positionNode = positionLocal.add(normalLocal.mul(thickness).mul(depthScale));
  m.side = THREE.BackSide;
  m.depthWrite = true;
  return m;
}

class Pool {
  constructor(scene, prim, cls) {
    this.scene = scene;
    this.prim = prim;
    this.cls = cls; // 'toon' | 'unlit' | 'hull'
    this.geometry = unitGeometryFor(prim);
    this.capacity = 0;
    this.count = 0;
    this.mesh = null;
    this._build(START_CAPACITY);
  }

  _build(capacity) {
    const old = this.mesh;
    this.capacity = capacity;

    const extra = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 3), 3);
    extra.setUsage(THREE.DynamicDrawUsage);

    let material;
    if (this.cls === 'hull') {
      material = makeHullMaterial(extra);
      this.thick = extra;
      this.color = null;
    } else {
      material = makePoolMaterial(this.cls);
      this.color = extra;
      this.thick = null;
    }

    const mesh = new THREE.InstancedMesh(this.geometry, material, capacity);
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    if (this.cls !== 'hull') mesh.instanceColor = extra;
    mesh.count = 0;
    // Instances move every frame; a bounding sphere computed from the unit
    // geometry would cull them all.
    mesh.frustumCulled = false;
    mesh.castShadow = this.cls === 'toon';
    mesh.receiveShadow = false;
    this.mesh = mesh;
    this.scene.add(mesh);

    if (old) {
      this.scene.remove(old);
      old.dispose();
      old.material.dispose();
    }
  }

  ensure(needed) {
    if (needed <= this.capacity) return;
    let cap = this.capacity;
    while (cap < needed) cap *= 2;
    this._build(cap);
  }

  begin() {
    this.count = 0;
  }

  commit() {
    const mesh = this.mesh;
    mesh.count = this.count;
    if (this.count > 0) {
      mesh.instanceMatrix.needsUpdate = true;
      const extra = this.color ?? this.thick;
      extra.needsUpdate = true;
    }
  }

  dispose() {
    this.scene.remove(this.mesh);
    this.mesh.dispose();
    this.mesh.material.dispose();
    this.geometry.dispose();
  }
}

const _m = new THREE.Matrix4();
const _dims = new THREE.Matrix4();
const _color = new THREE.Color();

export class CharacterBatcher {
  constructor(scene) {
    this.scene = scene;
    /** `${prim}|${cls}` -> Pool */
    this.pools = new Map();
    /** root Group -> flat part list (proxies from assembleCharacter). */
    this.roots = new Map();
    this.hullsVisible = true;
  }

  _pool(prim, cls) {
    const key = `${prim}|${cls}`;
    let pool = this.pools.get(key);
    if (!pool) {
      pool = new Pool(this.scene, prim, cls);
      pool.mesh.visible = cls !== 'hull' || this.hullsVisible;
      this.pools.set(key, pool);
    }
    return pool;
  }

  /** Registers one proxy-mode character root. Idempotent per root. */
  add(root) {
    const parts = root.userData.parts;
    if (!parts || this.roots.has(root)) return;
    // Cache each part's pool, dims matrix and hull thickness once.
    const entries = parts.map((node) => {
      const p = node.userData.part;
      return {
        node,
        pool: this._pool(p.prim, p.cls),
        hullPool: p.outline ? this._pool(p.prim, 'hull') : null,
        dims: new THREE.Matrix4().makeScale(p.dims[0], p.dims[1], p.dims[2]),
        colorHex: p.colorHex,
        // Converted ONCE. `Color.setHex` goes through ColorManagement (three
        // pow conversions), and this hex is fixed for the body's lifetime, so
        // doing it per part per frame was recomputing a constant ~320 times a
        // second. The lerp path below still needs a Color, but only when a
        // body is actually flashing.
        rgb: new THREE.Color().setHex(p.colorHex),
        // Pass 1 decides visibility; pass 2 re-asked the same question by
        // walking the same ancestor chain again.
        vis: false,
        thick: [
          HULL_THICKNESS / Math.max(p.dims[0], 1e-3),
          HULL_THICKNESS / Math.max(p.dims[1], 1e-3),
          HULL_THICKNESS / Math.max(p.dims[2], 1e-3),
        ],
      };
    });
    this.roots.set(root, entries);
  }

  remove(root) {
    this.roots.delete(root);
  }

  /** Show or hide every hull pool (the pipeline-outline tiers hide them). */
  setHullsVisible(visible) {
    this.hullsVisible = visible;
    for (const pool of this.pools.values()) {
      if (pool.cls === 'hull') pool.mesh.visible = visible;
    }
  }

  /**
   * Rewrites every pool from the posed hierarchies. Call once per frame,
   * AFTER the pose drivers have run and BEFORE the render.
   */
  update() {
    for (const pool of this.pools.values()) pool.begin();

    // Pass 1 — count, so pools can grow before any writes land.
    for (const [root, entries] of this.roots) {
      if (!root.visible || !root.parent) continue;
      for (const e of entries) {
        e.vis = this._shown(e.node, root);
        if (!e.vis) continue;
        e.pool.count += 1;
        if (e.hullPool) e.hullPool.count += 1;
      }
    }
    for (const pool of this.pools.values()) {
      pool.ensure(pool.count);
      pool.count = 0;
    }

    // Pass 2 — write matrices and colours.
    for (const [root, entries] of this.roots) {
      if (!root.visible || !root.parent) continue;
      root.updateWorldMatrix(true, true);
      const flash = Math.max(0, Math.min(1, root.userData.flashAmount ?? 0));
      for (const e of entries) {
        if (!e.vis) continue; // decided in pass 1
        _m.multiplyMatrices(e.node.matrixWorld, e.dims);

        const pool = e.pool;
        const i = pool.count++;
        _m.toArray(pool.mesh.instanceMatrix.array, i * 16);
        _color.copy(e.rgb);
        if (flash > 0) _color.lerp(_white, flash);
        _color.toArray(pool.color.array, i * 3);

        if (e.hullPool && this.hullsVisible) {
          const hp = e.hullPool;
          const j = hp.count++;
          _m.toArray(hp.mesh.instanceMatrix.array, j * 16);
          hp.thick.array[j * 3] = e.thick[0];
          hp.thick.array[j * 3 + 1] = e.thick[1];
          hp.thick.array[j * 3 + 2] = e.thick[2];
        }
      }
    }

    for (const pool of this.pools.values()) pool.commit();
  }

  /** Visibility along the proxy's ancestor chain (eyes vs X-eyes, hidden
   *  characters). The root itself is checked by the caller. */
  _shown(node, root) {
    for (let o = node; o && o !== root; o = o.parent) {
      if (!o.visible) return false;
    }
    return true;
  }

  /** For the mockup panel and outside measurement. */
  stats() {
    let instances = 0;
    let active = 0;
    for (const pool of this.pools.values()) {
      instances += pool.mesh.count;
      if (pool.mesh.count > 0) active += 1;
    }
    return { pools: this.pools.size, activePools: active, instances, batchedCharacters: this.roots.size };
  }

  dispose() {
    for (const pool of this.pools.values()) pool.dispose();
    this.pools.clear();
    this.roots.clear();
  }
}

const _white = new THREE.Color(0xffffff);

/** One batcher per scene in practice; the game and each mockup make their own. */
export function createCharacterBatcher(scene) {
  return new CharacterBatcher(scene);
}
